import { useState, useRef } from "react";
import { createBackup, restoreBackup } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Download, 
  Upload, 
  Database,
  AlertTriangle,
  CheckCircle,
  FileJson
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Backup() {
  const [loading, setLoading] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null);
  const fileInputRef = useRef(null);

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await createBackup();
      const backup = response.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_ap_depuratore_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup completato: ${backup.counts.partite} partite, ${backup.counts.movimenti} movimenti`);
    } catch (error) {
      console.error("Error creating backup:", error);
      toast.error("Errore nella creazione del backup");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        
        if (!backup.data || !backup.version) {
          toast.error("File di backup non valido");
          return;
        }
        
        setPendingRestore(backup);
        setRestoreConfirm(true);
      } catch (error) {
        toast.error("Errore nella lettura del file");
      }
    };
    reader.readAsText(file);
    
    // Reset input
    event.target.value = "";
  };

  const handleRestore = async () => {
    if (!pendingRestore) return;
    
    setLoading(true);
    setRestoreConfirm(false);
    
    try {
      const response = await restoreBackup(pendingRestore);
      const result = response.data;
      
      toast.success(`Ripristino completato: ${result.restored.partite || 0} partite, ${result.restored.movimenti || 0} movimenti`);
      setPendingRestore(null);
    } catch (error) {
      console.error("Error restoring backup:", error);
      toast.error("Errore nel ripristino del backup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
          Backup & Ripristino
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Gestisci i backup del database
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="card-industrial h-full">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-500" />
                Crea Backup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Scarica un file JSON contenente tutti i dati del sistema:
              </p>
              <ul className="text-sm text-slate-500 space-y-1 ml-4 list-disc">
                <li>Partite</li>
                <li>Movimenti (entrate/uscite)</li>
                <li>Fornitori</li>
                <li>Clienti</li>
                <li>Prodotti</li>
              </ul>
              <Button
                onClick={handleBackup}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Scarica Backup
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Restore Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="card-industrial h-full">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-500" />
                Ripristina Backup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Attenzione!</p>
                    <p>Il ripristino sovrascriverà TUTTI i dati esistenti.</p>
                  </div>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                variant="outline"
                className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <FileJson className="w-4 h-4 mr-2" />
                Seleziona File Backup
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreConfirm} onOpenChange={setRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Conferma Ripristino
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Stai per ripristinare un backup del <strong>{pendingRestore?.backup_date?.split('T')[0]}</strong>.</p>
              <p>Questo backup contiene:</p>
              <ul className="list-disc ml-4 text-sm">
                <li>{pendingRestore?.counts?.partite || 0} partite</li>
                <li>{pendingRestore?.counts?.movimenti || 0} movimenti</li>
                <li>{pendingRestore?.counts?.fornitori || 0} fornitori</li>
                <li>{pendingRestore?.counts?.clienti || 0} clienti</li>
                <li>{pendingRestore?.counts?.prodotti || 0} prodotti</li>
              </ul>
              <p className="font-medium text-red-600 mt-4">
                TUTTI i dati esistenti verranno eliminati e sostituiti!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Conferma Ripristino
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
